import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { unstable_usePrompt } from 'react-router-dom';
import styled, { css } from 'styled-components';

import HandlerIcon from '@/assets/icons/HandlerIcon';
import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import Transition from '@/components/Common/Transition';
import SimpleEditor from '@/features/Dashboard/components/DashboardCardContent/SimpleEditor';
import DashboardCardStyles from '@/features/Dashboard/components/styles/DashboardCardStyles';
import { DashboardTextContentStyle } from '@/features/Dashboard/components/styles/TextContentStyle';
import useColor from '@/hooks/domain/useColor';

import useReportCardDrag from '../../hooks/useReportCardDrag';
import useReportStore from '../../store/ReportStore';
import type { ReportColumnProps } from '../ReportEditor/ReportColumn';

const { StyledDragArea, StyledDashboardTextCard, StyledDashboardCardWrapper } =
  DashboardCardStyles;

const ReportTextItem = (props: ReportColumnProps) => {
  const originText = props.column.item.text;
  const setDragging = useReportStore((state) => state.setDragging);
  const updateTextItem = useReportStore((state) => state.updateTextItem);
  const removeColumn = useReportStore((state) => state.removeColumn);
  const mode = useReportStore((state) => state.mode);
  const isReportEditMode = mode === 'EDIT';
  if (!originText) throw new Error('Text is not exist');

  const color = useColor();
  const { index, column, width } = props;
  const { dragRef, previewRef, isDragging } = useReportCardDrag({
    index,
    setSomeDragging: setDragging,
    column,
  });

  const [text, setText] = useState(originText);
  const [editMode, setEditMode] = useState(false);

  const onEditDone = async () => {
    updateTextItem(column.item.alias, text);
    setEditMode(false);
  };

  const onCancel = () => {
    setText(props.column.item.text || '');
    setEditMode(false);
  };

  const onDelete = () => {
    removeColumn(column.row_alias, column);
  };

  useEffect(() => {
    if (!isReportEditMode) {
      setEditMode(false);
    }
  }, [isReportEditMode]);

  unstable_usePrompt({
    when: editMode,
    message: 'Changes you made may not be saved.',
  });

  if (!isReportEditMode)
    return (
      <StyledDashboardCardWrapper
        isTextCard={true}
        ref={previewRef}
        isDragging={isDragging}
        width={width}
      >
        <StyledDashboardTextCard>
          <Transition
            status={true}
            css={css`
              position: relative;
              display: flex;
              flex-direction: row;
              height: 100%;
              padding: 16px 0px 16px 16px;
              overflow: auto;
              .action-buttons {
                position: absolute;
                right: 24px;
                top: 24px;
                background-color: ${color.gray100};
              }
            `}
          >
            <TextDisplay
              className="ql-editor"
              style={{ flex: 1 }}
              dangerouslySetInnerHTML={{
                __html: text,
              }}
            />
          </Transition>
        </StyledDashboardTextCard>
      </StyledDashboardCardWrapper>
    );

  if (!editMode)
    return (
      <StyledDashboardCardWrapper
        isTextCard={true}
        ref={previewRef}
        isDragging={isDragging}
        width={width}
      >
        <StyledDashboardTextCard>
          <StyledDragArea
            ref={dragRef}
            className="drag-area-handler"
            style={{ top: 6 }}
          >
            <HandlerIcon />
          </StyledDragArea>

          <Transition
            status={true}
            css={css`
              position: relative;
              display: flex;
              flex-direction: row;
              height: 100%;
              padding: 16px 0px 16px 16px;
              overflow: auto;
              .action-buttons {
                position: absolute;
                right: 24px;
                top: 24px;
                background-color: ${color.gray100};
              }
            `}
            onClick={() => isReportEditMode && setEditMode(true)}
          >
            <TextDisplay
              className="ql-editor"
              style={{ flex: 1 }}
              dangerouslySetInnerHTML={{
                __html: text,
              }}
            />
            <Flex className="action-buttons" g={8}>
              <IconButton
                outlined={false}
                icon={<EditOutlined />}
                sizeType="m"
                onClick={() => setEditMode(true)}
              />
              <IconButton
                outlined={false}
                icon={<DeleteOutlined />}
                sizeType="m"
                onClick={onDelete}
              />
            </Flex>
          </Transition>
        </StyledDashboardTextCard>
      </StyledDashboardCardWrapper>
    );

  return (
    <Style style={{ width: width, maxHeight: 800 }} g={8}>
      <SimpleEditor
        value={text}
        onChange={(e) => {
          setText(e);
        }}
        wrapperStyle={{
          backgroundColor: color.gray200,
          padding: '0px 24px 8px 24px',
          borderRadius: 16,
          height: '100%',
          overflow: 'auto',
          flex: 1,
        }}
        textStyleCss={DashboardTextContentStyle}
      />
      <Flex j="space-between">
        <Button variant="error_outlined" onClick={onDelete}>
          Delete
        </Button>
        <Flex g={8} j="flex-end">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onEditDone}>Done</Button>
        </Flex>
      </Flex>
    </Style>
  );
};

const Style = styled(VFlex)`
  flex: 1;
  flex-shrink: 1;
  min-height: 140px;
  overflow: auto;
  padding: 0px 12px;
`;

const TextDisplay = styled.div`
  ${DashboardTextContentStyle};
`;

export default ReportTextItem;
